import { Plus } from "lucide-react";
import { useNavigate } from "react-router-dom";
import {
	BillingModeBadge,
	ClientStatusBadge,
} from "@/components/clients/ClientBadges";
import { Button } from "@/components/ui/button";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";
import { useClients } from "@/hooks/useClients";
import { useMe } from "@/hooks/useMe";

export const ClientsPage = () => {
	const navigate = useNavigate();
	const { data: clients, isLoading } = useClients();
	const { data: me } = useMe();

	// Display-side gate only — the server enforces owner/admin regardless.
	const canProvision =
		me?.member.role === "owner" || me?.member.role === "admin";

	return (
		<div className="space-y-4">
			<div className="flex items-center justify-between">
				<h1 className="text-2xl font-bold text-white">Clients</h1>
				{canProvision && (
					<Button onClick={() => navigate("/clients/new")}>
						<Plus className="h-4 w-4" />
						New client
					</Button>
				)}
			</div>

			<div className="bg-clyr-gray-800 rounded-lg border border-clyr-gray-700 overflow-hidden">
				<Table>
					<TableHeader>
						<TableRow className="border-clyr-gray-700 text-xs uppercase">
							<TableHead className="text-clyr-gray-400">Name</TableHead>
							<TableHead className="text-clyr-gray-400">Status</TableHead>
							<TableHead className="text-clyr-gray-400">Billing</TableHead>
							<TableHead className="text-clyr-gray-400">Subscription</TableHead>
							<TableHead className="text-right text-clyr-gray-400">
								Members
							</TableHead>
							<TableHead className="text-clyr-gray-400">Created</TableHead>
						</TableRow>
					</TableHeader>
					<TableBody>
						{isLoading ? (
							<TableRow>
								<TableCell
									colSpan={6}
									className="py-8 text-center text-clyr-gray-500"
								>
									Loading...
								</TableCell>
							</TableRow>
						) : !clients?.length ? (
							<TableRow>
								<TableCell
									colSpan={6}
									className="py-8 text-center text-clyr-gray-500"
								>
									No clients yet — provision your first one with “New client”
								</TableCell>
							</TableRow>
						) : (
							clients.map((client) => (
								<TableRow
									key={client.id}
									onClick={() => navigate(`/clients/${client.id}`)}
									className="border-clyr-gray-700/50 hover:bg-clyr-gray-700/30 cursor-pointer transition-colors"
								>
									<TableCell className="text-white font-medium">
										{client.name ?? "—"}
									</TableCell>
									<TableCell>
										<ClientStatusBadge client={client} />
									</TableCell>
									<TableCell>
										<BillingModeBadge mode={client.partnerBillingMode} />
									</TableCell>
									<TableCell className="text-clyr-gray-400 capitalize">
										{client.stripeSubscriptionStatus ?? "—"}
									</TableCell>
									<TableCell className="text-right text-clyr-gray-300 tabular-nums">
										{client.memberCount.toLocaleString()}
									</TableCell>
									<TableCell className="text-clyr-gray-400">
										{new Date(client.createdAt).toLocaleDateString()}
									</TableCell>
								</TableRow>
							))
						)}
					</TableBody>
				</Table>
			</div>
		</div>
	);
};
